import {
  RiskTierDirection,
  RiskTierName
} from '../../../Api/Admin/ProjectManagementConfigs/projectManagementConfigsApi';

export interface DayThresholdRange {
  min: number;
  max: number | null;
}

// Inline hex, not Tailwind bg-*-500: the app config overrides `red` to a scalar,
// so `bg-red-500` never generates. Hex keeps all three circles reliable.
export const TIER_CIRCLE_COLORS: Record<RiskTierName, string> = {
  green: '#1FAC4B',
  yellow: '#EAA300',
  red: '#DC322F'
};

const days = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`;

// "0 days" | "1 day" | "1–14 days" | "15+ days"
export const formatDayThreshold = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return '';
  if (max === null) return `${min}+ days`;
  if (min === null || min === max) return days(max);
  return `${min}–${max} days`;
};

// Accepts what formatDayThreshold produces, with or without the "days"
// suffix and with either the en-dash or hyphen: "0", "1-14 days", "15+".
export const parseDayThreshold = (text: string): DayThresholdRange | null => {
  const value = text.replace(/days?/i, '').trim();

  const range = value.match(/^(\d+)\s*[–-]\s*(\d+)$/);
  if (range) {
    const min = Number(range[1]);
    const max = Number(range[2]);
    return max >= min ? { min, max } : null;
  }

  const openEnded = value.match(/^(\d+)\s*\+$/);
  if (openEnded) return { min: Number(openEnded[1]), max: null };

  const single = value.match(/^(\d+)$/);
  if (single) return { min: Number(single[1]), max: Number(single[1]) };

  return null;
};

interface ThresholdRow {
  tier: RiskTierName;
  thresholdText: string;
  direction: RiskTierDirection;
  primaryDateField: string;
  comparisonDateField: string;
}

const pairKey = (row: ThresholdRow) => `${row.primaryDateField}\0${row.comparisonDateField}`;

const flaggingTiers = (rows: ThresholdRow[]) => rows.filter((row) => row.tier !== 'green');

const pastTiers = (rows: ThresholdRow[]) => flaggingTiers(rows).filter((row) => row.direction === 'past');

// Ascending severity, the one source for severity-ordered UI here and in the risk widgets.
export const TIER_SEVERITY: RiskTierName[] = ['green', 'yellow', 'red'];

const severityOf = (tier: RiskTierName) => TIER_SEVERITY.indexOf(tier);

// Mirrors the API's worst_past_on_pair?: only a worse past tier on the same pair takes over at a maximum.
export const runsOnPastMax = (rows: ThresholdRow[], row: ThresholdRow): boolean =>
  row.tier !== 'green' &&
  row.direction === 'past' &&
  !pastTiers(rows).some((other) => severityOf(other.tier) > severityOf(row.tier) && pairKey(other) === pairKey(row));

// A late service fails green, so with no Past tier to catch it it gets no mark at all.
export const hasNoPastTier = (rows: ThresholdRow[]): boolean =>
  flaggingTiers(rows).length > 0 && pastTiers(rows).length === 0;

// A chain may open on day 0, which fires on the comparison date itself, or on day 1.
const LATEST_FLOOR = 1;

// A gap leaves a late day with no risk mark rather than failing, so warn, never block.
const chainHasGap = (rows: ThresholdRow[]): boolean => {
  const ranges = rows
    .map((row) => parseDayThreshold(row.thresholdText))
    .filter((range): range is DayThresholdRange => range !== null)
    .sort((a, b) => a.min - b.min);

  if (ranges.length === 0) return false;
  if (ranges[0].min > LATEST_FLOOR) return true;

  return ranges.some((range, index) => {
    const next = ranges[index + 1];
    if (!next) return false;
    return range.max === null || next.min !== range.max + 1;
  });
};

// Each date pair is its own axis. Green is the fallback, not a band on any of them.
export const hasNonSequentialThresholds = (rows: ThresholdRow[]): boolean => {
  const pastRows = pastTiers(rows);

  return [...new Set(pastRows.map(pairKey))].some((key) => chainHasGap(pastRows.filter((row) => pairKey(row) === key)));
};

// A row late by a pair no Past tier thresholds fails green too, and gets no mark at all.
export const greenPairHasNoThreshold = (rows: ThresholdRow[]): boolean => {
  const green = rows.find((row) => row.tier === 'green');
  const pastRows = pastTiers(rows);
  if (!green || pastRows.length === 0) return false;

  return !pastRows.some((row) => pairKey(row) === pairKey(green));
};

export const unthresholdedPairWarning = (tierLabel: string): string =>
  `${tierLabel} compares two dates no tier set to Past measures. A service late by those two, but not flagged by ` +
  'the others, is left with no risk mark at all. Set a tier on those dates to Past, or point ' +
  `${tierLabel} at a pair one measures.`;

// The API's green_holds? refuses a delivery earlier than green's window.
export const onTrackWindowDays = (rows: ThresholdRow[]): number | null => {
  const green = rows.find((row) => row.tier === 'green');
  if (!green || green.direction !== 'before') return null;

  const range = parseDayThreshold(green.thresholdText);
  if (!range?.max || range.max <= 0) return null;
  return beforeTiersCoverFrom(rows, green, range.max + 1) ? null : range.max;
};

// Whether tiers measured before on green's pair mark every day from `firstDay` ahead onwards. A
// single number reads as a lead time from day zero, as the API's window_min does.
const beforeTiersCoverFrom = (rows: ThresholdRow[], green: ThresholdRow, firstDay: number): boolean => {
  const windows = flaggingTiers(rows)
    .filter((row) => row.direction === 'before' && pairKey(row) === pairKey(green))
    .map((row) => parseDayThreshold(row.thresholdText))
    .filter((range): range is DayThresholdRange => range !== null)
    .map((range) => ({ min: range.min === range.max ? 0 : range.min, max: range.max }))
    .sort((a, b) => a.min - b.min);

  let next = firstDay;
  for (const window of windows) {
    if (window.min > next) return false;
    if (window.max === null) return true;
    next = Math.max(next, window.max + 1);
  }
  return false;
};

export const onTrackWindowWarning = (tierLabel: string, windowDays: number): string =>
  `${tierLabel} holds only for a service delivered up to ${days(windowDays)} early; anything earlier is left ` +
  'with no risk mark unless a tier measured Before catches it. Set it to 0 days to keep every early delivery on track.';

// Mirrors ProjectRisk::Config#threshold_phrase word for word, so keep the two in step.
export const thresholdPhrase = (
  range: DayThresholdRange | null,
  direction: RiskTierDirection,
  tier: RiskTierName = 'green',
  runsOn = false
): string => {
  if (!range) return '';
  if (direction === 'past') return rangePhrase(runsOn ? { ...range, max: null } : range, 'past');

  // A lead time runs from the date itself: green's window and a single number start at zero.
  const windowMin = tier === 'green' || range.min === range.max ? 0 : range.min;
  if (windowMin > 0) return rangePhrase(range, 'before');
  if (range.max !== null && range.max > 0) return `is no more than ${days(range.max)} before`;
  // Green holds on any earlier date; a yellow or red window of zero fires on the day alone.
  if (range.max === 0 && tier !== 'green') return 'is on the same day as';
  return 'is on or before';
};

const rangePhrase = ({ min, max }: DayThresholdRange, side: RiskTierDirection): string => {
  if (max === null) return `is ${min} or more days ${side}`;
  if (min === max) return `is ${days(min)} ${side}`;
  return `is ${min} to ${max} days ${side}`;
};

// The label names the tier to everyone reading the card, but it is the admin's to blank out.
export const tierName = (row: { tier: RiskTierName; label: string }) => row.label.trim() || row.tier;

const listOf = (labels: string[]) => {
  const leading = labels.slice(0, -1).join(', ');
  return leading ? `${leading} and ${labels[labels.length - 1]}` : labels[0];
};

// Blocking, so name the tier rather than rely on an outline the admin may have scrolled past.
export const sameDateWarning = (tierLabels: string[]): string => {
  if (tierLabels.length === 0) return '';

  const one = tierLabels.length === 1;

  return (
    `${listOf(tierLabels)} ${one ? 'measures one date against itself' : 'measure one date against themselves'}, ` +
    `which cannot be saved. Pick two different dates for ${one ? 'it' : 'each'}.`
  );
};

export interface UnavailableDateWarningOptions {
  // False while the catalogue is empty, when telling anyone to pick a replacement would be a lie.
  canReplace?: boolean;
  // True when the account is taking over an inherited config, where the pair cannot be omitted.
  blocksSave?: boolean;
}

// A withdrawn date isn't reverted to a default, so name the tiers that stopped measuring.
export const unavailableDateWarning = (
  tierLabels: string[],
  { canReplace = true, blocksSave = false }: UnavailableDateWarningOptions = {}
): string => {
  if (tierLabels.length === 0) return '';

  const one = tierLabels.length === 1;
  const replacement = one ? 'a replacement date for it' : 'replacement dates for them';
  const instruction = canReplace
    ? ` Pick ${replacement}${blocksSave ? ' to save' : ''}.`
    : blocksSave
      ? ` ${one ? 'It cannot be saved until a replacement is' : 'They cannot be saved until replacements are'} picked.`
      : '';

  return (
    `${listOf(tierLabels)} ${one ? 'measures a date that is' : 'measure dates that are'} no longer available for ` +
    `this account, so ${one ? 'it currently flags' : 'they currently flag'} nothing.${instruction}`
  );
};
